import React, { useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

export interface ExerciseDropdownProps {
  exercises: Array<{ id: number; name: string }>;
  value: number | null;
  onChange: (value: number | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  showClear?: boolean;
  /**
   * Cuando esta presente, el dropdown muestra un footer "Crear '<query>'"
   * que invoca este callback con el filterValue actual. Usado por el flujo
   * de import IA del coach para crear ejercicios faltantes inline.
   */
  onCreateNew?: (query: string) => void;
}

export const ExerciseDropdown: React.FC<ExerciseDropdownProps> = ({
  exercises,
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder = 'Seleccionar ejercicio',
  className = 'w-full p-inputtext-sm',
  showClear = true,
  onCreateNew
}) => {
  const [filterValue, setFilterValue] = useState('');

  const sortedExercises = useMemo(() => {
    if (!filterValue) return exercises;
    const lower = filterValue.toLowerCase();
    return [...exercises].sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aExact = aName === lower;
      const bExact = bName === lower;
      if (aExact && !bExact) return -1;
      if (bExact && !aExact) return 1;
      const aStarts = aName.startsWith(lower);
      const bStarts = bName.startsWith(lower);
      if (aStarts && !bStarts) return -1;
      if (bStarts && !aStarts) return 1;
      return aName.localeCompare(bName);
    });
  }, [exercises, filterValue]);

  const panelFooter = onCreateNew
    ? () => {
        const query = filterValue.trim();
        if (query.length === 0) return null;
        return (
          <div
            style={{
              padding: '0.5rem 0.75rem',
              borderTop: '1px solid var(--ios-divider)',
              background: 'var(--ios-surface-subtle)'
            }}
          >
            <Button
              icon="pi pi-plus"
              label={`Crear "${query}"`}
              className="p-button-text p-button-sm w-full"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCreateNew(query);
              }}
              style={{
                justifyContent: 'flex-start',
                color: '#6366f1',
                fontWeight: 600
              }}
            />
          </div>
        );
      }
    : undefined;

  return (
    <Dropdown
      className={className}
      options={sortedExercises}
      optionLabel="name"
      optionValue="id"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.value)}
      filter
      showClear={showClear}
      filterInputAutoFocus={false}
      resetFilterOnHide
      onFilter={(e: { filter: string }) => setFilterValue(e.filter)}
      onHide={() => setFilterValue('')}
      onFocus={onFocus}
      onBlur={onBlur}
      // Virtualiza el panel para catálogos grandes (vimos 1036 ejercicios en prod).
      // Ver qa-audit/2026-05-21/coach/03-create-plan.md (Bug 07).
      virtualScrollerOptions={{ itemSize: 38 }}
      emptyMessage="No se encontraron ejercicios"
      emptyFilterMessage="No se encontraron ejercicios"
      panelFooterTemplate={panelFooter}
    />
  );
};
